import React, { useState } from 'react';
import styled from 'styled-components';

import * as config from 'utils/config';
import { getAvatarURL } from 'utils/cloudinary';
import * as Amplitude from 'utils/amplitude';

import * as AuthUtils from 'utils/auth';
import { allowMarketplace } from 'utils/employee';

import {
  HistoryRoutes,
  ClaimRoutes,
  StoreRoutes,
  AccountRoutes,
  SettingsRoutes,
  RecognitionRoutes,
} from 'utils/routeGenerator';

import { Avatar } from 'antd';
import {
  SVG_ICON_SIZES,
  MENU_HORIZONTAL_ALIGNMENTS,
} from '@twicapp/ui/constants/properties';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

import { ReactComponent as SettingSvg } from '@twicapp/ui/icons/settings.svg';
import { ReactComponent as TimerSvg } from '@twicapp/ui/icons/timer.svg';
import { ReactComponent as DocumentSvg } from '@twicapp/ui/icons/document.svg';
import { ReactComponent as DocumentsSvg } from '@twicapp/ui/icons/documents.svg';
import { ReactComponent as LogOutSvg } from '@twicapp/ui/icons/logout.svg';
import { ReactComponent as CaretDownSvg } from '@twicapp/ui/icons/caret-down.svg';
import { ReactComponent as StoreSvg } from '@twicapp/ui/icons/store.svg';
import { ReactComponent as WalletSvg } from '@twicapp/ui/icons/wallet.svg';
import { ReactComponent as StarSvg } from '@twicapp/ui/icons/star.svg';
import { ReactComponent as FloatSvg } from '@twicapp/ui/icons/float.svg';

import useSnackbar from '@twicapp/ui/hooks/useSnackbar';

import Menu from '@twicapp/ui/components/Menu';
import MenuLabel from '@twicapp/ui/components/MenuLabel';
import MenuItem from '@twicapp/ui/components/MenuItem';
import SvgIcon from '@twicapp/ui/components/SvgIcon';

const DownArrowIcon = styled(SvgIcon).attrs({
  icon: CaretDownSvg,
  size: SVG_ICON_SIZES.MEDIUM,
})``;

const ProfilePic = styled(Avatar).attrs({
  style: { marginRight: 5 },
})``;

const Container = styled.div`
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
`;

const AvatarContainer = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 100%;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({
    theme: {
      colors: {
        border: { primary },
      },
    },
  }) => primary};
  width: 100%;
  margin: 12px 0;

  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: none;
  }
`;

const MobileMenuLabel = styled(MenuLabel)`
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: none;
  }
`;

const MobileMenuItem = styled(MenuItem)`
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: none;
  }
`;

const TopNavUser = ({
  company,
  employee,
  pathname,
  show_store,
  show_claims,
  show_recognitions,
}: $TSFixMe) => {
  const [is_open, setIsOpen] = useState(false);
  const { reimbursement_policy_link } = company.settings;
  const { showPlainSnackbar } = useSnackbar();

  const doLogOut = () => {
    showPlainSnackbar({ text: 'Logging out...' });
    AuthUtils.removeAuthCookie();
    AuthUtils.removeEmployeeIdCookie();
    window.location.href = config.getLogInRedirectUrl();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  const avatar =
    employee.avatar_url ||
    'https://s3-us-west-1.amazonaws.com/twic.ai/avatar/default_avatar.png';

  return (
    <Container>
      <AvatarContainer
        onClick={() => setIsOpen((current_is_open) => !current_is_open)}
        onKeyDown={handleKeyPress}
        aria-haspopup="true"
        aria-expanded={is_open ? 'true' : 'false'}
        aria-controls="user-menu"
        id="user-menu-button"
      >
        <ProfilePic
          src={getAvatarURL(avatar, { width: 40, height: 40 })}
          alt="Profile Avatar"
        />
        <DownArrowIcon />
      </AvatarContainer>
      <Menu
        id="user-menu"
        aria-labelledby="user-menu-button"
        open={is_open}
        onClose={() => setIsOpen(false)}
        horizontal_alignment={MENU_HORIZONTAL_ALIGNMENTS.RIGHT}
      >
        <MobileMenuLabel>Twic Experience</MobileMenuLabel>
        {show_store && (
          <MobileMenuItem
            start_text="Store"
            start_icon={StoreSvg}
            to={
              allowMarketplace(employee, company)
                ? StoreRoutes.listExplore()
                : StoreRoutes.detailPretax({
                    pretax_account: 'home',
                  })
            }
          />
        )}
        <MobileMenuItem
          start_text="Accounts"
          start_icon={WalletSvg}
          to={AccountRoutes.list()}
        />
        {show_claims && (
          <MobileMenuItem
            start_text="Claims"
            start_icon={DocumentSvg}
            to={ClaimRoutes.list()}
          />
        )}
        {show_recognitions && (
          <MobileMenuItem
            start_text="Recognitions"
            start_icon={StarSvg}
            to={RecognitionRoutes.list()}
          />
        )}
        <Divider />
        <MobileMenuLabel>Manage Your Profile</MobileMenuLabel>
        <MenuItem
          start_text="Settings"
          start_icon={SettingSvg}
          to={SettingsRoutes.root()}
        />
        <MenuItem
          start_text="History"
          start_icon={TimerSvg}
          to={HistoryRoutes.orderList()}
        />
        <MenuItem
          start_text="Program Policy"
          start_icon={DocumentsSvg}
          onClick={() => {
            window.open(reimbursement_policy_link);
            Amplitude.logEvent(Amplitude.EVENTS.ProgramPolicyViewed, {
              from_screen: 'profile_menu',
            });
          }}
        />
        <MenuItem
          start_text="Help Center"
          start_icon={FloatSvg}
          onClick={() => {
            window.open('https://support.twic.ai/en/');
            Amplitude.logEvent(Amplitude.EVENTS.HelpCenterViewed, {
              url: pathname,
            });
          }}
        />
        <MenuItem
          start_text="Log out"
          start_icon={LogOutSvg}
          onClick={() => doLogOut()}
        />
      </Menu>
    </Container>
  );
};

export default TopNavUser;
